import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  Outlet,
} from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./css/toastify-custom.css";
import "./App.css";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import useMediaQuery from "@mui/material/useMediaQuery";
import axios from "axios";

import theme from "./theme";

import { AppProvider } from "./context/AppContext";
import { LimitsProvider, useLimits } from "./context/LimitsContext";
import { LanguageProvider, useLanguage } from './context/LanguageContext';

import authApi from "./api/authApi";
import TopBar from "./components/TopBar";
import Sidenav from "./components/Sidenav";
import Login from "./components/Login";
import PasswordReset from "./components/PasswordReset";
import ResetPasswordForm from "./components/ResetPasswordForm";
import AuthLayout from "./components/AuthLayout";
import Layout from "./components/Layout";
import Profile from "./components/Profile";
import ProjectScreen from "./components/Project/ProjectsScreen";
import { io } from "socket.io-client";

import config from "./config/env";
import Dashboard from "./components/Dashboard";
import VideoScreen from "./components/videoscreen/VideoScreen";
import Settings from './components/Settings';
import SubscriptionModal from './components/common/SubscriptionModal';
import { setGlobalShowSubscriptionModal } from './api/generalApi';
import Chat from "./components/Chat";
import Register from "./components/Register";
import { AUTH_STATE } from './api/authStateManager';
import BotChat from "./components/BotChat";
import bots from "./const/bots.json";
import LanguageModal from './components/LanguageModal';
import ImageGeneration from './components/ImageGeneration';

const SOCKET_BE_URL = config.SOCKET_BE_URL;

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSidenavOpen, setIsSidenavOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [isRequiredLanguageSelection, setIsRequiredLanguageSelection] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { fetchLimits } = useLimits();
  const { language, loading: languageLoading, needsLanguageSelection } = useLanguage();
  const location = useLocation();
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  const [notifications, setNotifications] = useState({
    jobNotifications: [],
    marketingNotifications: [],
  });
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionUrl, setSubscriptionUrl] = useState('');

  // Initialize interceptor once
  useEffect(() => {
    authApi.setupInterceptors();
  }, []);

  // Auth check effect
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          const { exp } = JSON.parse(jsonPayload);
          
          if (exp * 1000 < Date.now()) {
            authApi.setAuthToken(null);
            setIsAuthenticated(false);
          } else {
            const isAuth = authApi.checkAuthStatus();
            setIsAuthenticated(isAuth);
          }
        } catch (error) {
          authApi.setAuthToken(null);
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  // Show language modal when authenticated and no language is set
  useEffect(() => {
    if (isAuthenticated && !languageLoading && needsLanguageSelection) {
      setShowLanguageModal(true);
      setIsRequiredLanguageSelection(true);
    }
  }, [isAuthenticated, languageLoading, needsLanguageSelection]);

  // Listen for language update events
  useEffect(() => {
    const handleLanguageUpdated = () => {
      setShowLanguageModal(false);
      setIsRequiredLanguageSelection(false);
    };

    const handleOpenLanguageModal = () => {
      setShowLanguageModal(true);
      setIsRequiredLanguageSelection(false); // When opened manually, it's not required
    };

    window.addEventListener('languageUpdated', handleLanguageUpdated);
    window.addEventListener('openLanguageModal', handleOpenLanguageModal);
    
    return () => {
      window.removeEventListener('languageUpdated', handleLanguageUpdated);
      window.removeEventListener('openLanguageModal', handleOpenLanguageModal);
    };
  }, []);

  // Socket effect
  useEffect(() => {
    const connectSocket = () => {
      if (!isAuthenticated) return;
      
      const token = localStorage.getItem("token");
      if (!token) return;

      const baseUrl = SOCKET_BE_URL.replace(/\/api\/?$/, '').replace(/\/$/, '');
      
      const newSocket = io(baseUrl, {
        auth: { token },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        transports: ['websocket', 'polling'],
        path: '/socket.io',
        autoConnect: false,
        withCredentials: true,
      });

      newSocket.on("connect_error", (error) => {
        console.log("Connection error:", error);
      });

      newSocket.connect();
      setSocket(newSocket);
      socketRef.current = newSocket;
    };

    if (isAuthenticated) {
      connectSocket();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
    };
  }, [isAuthenticated]);

  // Auth state change handler
  useEffect(() => {
    const handleAuthStateChange = (event) => {
      const { state } = event.detail;
      
      setIsAuthenticated(state === AUTH_STATE.ACTIVE);
      
      if (state !== AUTH_STATE.ACTIVE) {
        // Clean up socket
        if (socketRef.current) {
          socketRef.current.removeAllListeners();
          socketRef.current.disconnect();
          socketRef.current = null;
          setSocket(null);
        }
        
        setNotifications({
          jobNotifications: [],
          marketingNotifications: [],
        });
      }
    };

    window.addEventListener("authStateChanged", handleAuthStateChange);
    return () => {
      window.removeEventListener("authStateChanged", handleAuthStateChange);
    };
  }, []);

  useEffect(() => {
    setGlobalShowSubscriptionModal((url) => {
      setSubscriptionUrl(url);
      setSubscriptionModalOpen(true);
    });
  }, []);

  // New useEffect for updating the tab title
  useEffect(() => {
    const path = location.pathname;
    let title = "SpeareAI";

    if (path.startsWith("/chat/")) {
      const botId = parseInt(path.split("/").pop());
      const bot = bots.find(b => b.botId === botId);
      if (bot) {
        title = `Chat with ${bot.botName} | SpeareAI`;
      }
    } else {
      switch (path) {
        case "/":
        case "/dashboard":
          title = "AI Experts Dashboard | SpeareAI";
          break;
        case "/login":
          title = "Login | SpeareAI";
          break;
        case "/register":
          title = "Register | SpeareAI";
          break;
        case "/reset-password":
          title = "Reset Password | SpeareAI";
          break;
        case "/reset":
          title = "Reset Password | SpeareAI";
          break;
        case "/projects":
          title = "My Projects | SpeareAI";
          break;
        case "/training":
          title = "Training Videos | SpeareAI";
          break;
        case "/profile":
          title = "My Profile | SpeareAI";
          break;
        case "/images":
          title = "AI Image Generation | SpeareAI";
          break;
        case "/settings":
          title = "Account Settings | SpeareAI";
          break;
        case "/chat":
          title = "Chat History | SpeareAI";
          break;
        default:
          if (path.startsWith("/social-first-blog-posts/")) {
            title = "Blog Post Editor | SpeareAI";
          } else if (path.startsWith("/dashboard/")) {
            title = "Keyword Analysis | SpeareAI";
          }
      }
    }

    document.title = title;
  }, [location]);

  const handleLogin = (token) => {
    authApi.setAuthToken(token);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    authApi.logout();
    setIsAuthenticated(false);
  };

  const handleSidenavToggle = () => {
    setIsSidenavOpen(!isSidenavOpen);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const showTopBar =
    isAuthenticated &&
    !["/reset", "/reset-password"].includes(location.pathname);

  return (
    <>
      <div className="App">
        {showTopBar && (
          <>
            <TopBar
              isSidenavOpen={isSidenavOpen}
              onMenuClick={handleSidenavToggle}
              onLogout={handleLogout}
              isMobile={isMobile}
              notifications={notifications}
              setNotifications={setNotifications}
            />
            <Sidenav
              open={isSidenavOpen}
              onClose={() => setIsSidenavOpen(false)}
              isMobile={isMobile}
            />
          </>
        )}
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AuthLayout isAuthenticated={isAuthenticated}>
                  <Login onLogin={handleLogin} />
                </AuthLayout>
              )
            }
          />
          <Route
            path="/register"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AuthLayout isAuthenticated={isAuthenticated}>
                  <Register />
                </AuthLayout>
              )
            }
          />
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route path="/reset" element={<ResetPasswordForm />} />
          
          {/* Root redirect */}
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Protected Routes */}
          <Route
            element={
              isAuthenticated ? (
                <Layout isMobile={isMobile}>
                  <Outlet />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/projects" element={<ProjectScreen />} />
            <Route path="/training" element={<VideoScreen />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/images" element={<ImageGeneration />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/chat/:botId" element={<BotChat />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
        <SubscriptionModal 
          open={subscriptionModalOpen}
          onClose={() => setSubscriptionModalOpen(false)}
          subscriptionUrl={subscriptionUrl}
        />
        <LanguageModal 
          open={showLanguageModal} 
          onClose={() => setShowLanguageModal(false)} 
          isRequired={isRequiredLanguageSelection}
        />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppProvider>
        <LimitsProvider>
          <LanguageProvider>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <div className="app-wrapper">
                <div className="background-container"></div>
                <AppContent />
              </div>
            </ThemeProvider>
          </LanguageProvider>
        </LimitsProvider>
      </AppProvider>
    </Router>
  );
}

export default App;
